import { ChangeDetectorRef, Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor, FormArray,
	FormBuilder, FormControl,
	FormGroup,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
	Validators
} from '@angular/forms';
import { NotificationItem } from '../../../api/notification/notification-item.model';
import { OrganizationManagerService } from '../../../api/organization/organization-manager.service';
import { Organization } from '../../../api/organization/organization.model';
import { debounceTime, map } from 'rxjs/operators';
import { MyCampApp } from '../../../api/common/enumerations';
import { ValidationUtil } from '../../../core-ui/utils/validation-util';
import * as moment from 'moment/moment';
import { MemberCountWithCategoriesResult } from '../../../api/member/member-count-with-categories-result.model';
import { CategoryItem } from '../../../api/categories/category-item.model';
import { MemberManagerService } from '../../../api/member/member-manager.service';
import { DeviceManagerService } from '../../../core/services/device-manager.service';
import { Editor } from 'primeng/editor';
import { ImageManagerService } from '../../../api/images/image-manager.service';
import { SelectPDFComponent } from '../../../core-ui/select-pdf/select-pdf.component';
import { SelectImagesComponent } from '../../../core-ui/select-images/select-images.component';
import RegistrationFilterType = MyCampApp.RegistrationFilterType;
import NotificationType = MyCampApp.NotificationType;
import { NotificationSendInfo } from '../../../api/notification/notification-send-info.model';
import { AppTeamUtil } from '../../../core/app-team-util';
import Quill from 'quill';

@Component({
	selector: 'mycampapp-edit-notification-control',
	templateUrl: './edit-notification-control.component.html',
	styleUrls: ['./edit-notification-control.component.less'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => EditNotificationControlComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => EditNotificationControlComponent),
			multi: true
		}
	]
})
export class EditNotificationControlComponent implements OnInit, ControlValueAccessor, Validator
{
	@ViewChild('fileInput', {static: false}) fileInput: any;
	@ViewChild('htmlEditor', {static: false}) htmlEditor?: Editor;
	@ViewChild('selectImage', { static: false }) selectImage!: SelectImagesComponent;
	@ViewChild('selectPdf', { static: false }) selectPdf!: SelectPDFComponent;

	@Input('allowSms') allowSms = true;
	@Input('allowPdf') allowPdf = true;
	@Input('hideCategories') hideCategories = false;
	@Input('value') notification!: NotificationItem;

	disabled = false;
	editForm!: FormGroup;
	memberCountResult?: MemberCountWithCategoriesResult;
	organization!: Organization;
	showSelectCategoriesDialog = false;
	textMaxLength = 0;
	titleMaxLength = 150;
	soundChoices = [
		{ label: 'Phone default', value: 'default' },
		{ label: 'Camera', value: 'camera.wav' },
		{ label: 'Ding', value: 'harp.wav' },
		{ label: 'Yay!', value: 'yay.wav' },
	];
	typeChoices = [
		{ label: 'Mobile Push Notification', value: NotificationType.Push },
		{ label: 'Email', value: NotificationType.Email },
		{ label: 'Text Message', value: NotificationType.Sms }
	];
	registeredChoices = [
		{ label: 'All', value: RegistrationFilterType.All },
		{ label: 'Registered', value: RegistrationFilterType.Registered },
		{ label: 'Unregistered', value: RegistrationFilterType.Unregistered },
	]
	quillModules = { clipboard: { matchVisual: false } };

	ImageValidationMessage?: string;
	TitleValidationMessage?: string;
	TextValidationMessage?: string;
	ScheduledDateValidationMessage: any[] = [];

	NotificationType = NotificationType;

	private controlObjectPairs: any[] = [];
	private quill: any;
	private updatingForm = false;
	private SOUND_PATH = 'https://generalmyappstorage.blob.core.windows.net/sounds/';

	onTouched: () => void = () => {};

	constructor(private fb: FormBuilder,
	            private changeDetector: ChangeDetectorRef,
				public deviceManager: DeviceManagerService,
				private imageManager: ImageManagerService,
				private memberManager: MemberManagerService,
	            private organizationManager: OrganizationManagerService) { }

	ngOnInit(): void
	{
		this.organizationManager.currentOrganization.subscribe(org => this.organization = org);
		this.editForm = this.fb.group({
			Categories: [],
			HtmlText: undefined,
			MediaUrl: undefined,
			PdfFile: undefined,
			PdfUrl: undefined,
			PhotoFile: undefined,
			RegistrationFilter: RegistrationFilterType.All,
			ScheduledDateTimes: this.fb.array([]),
			// ScheduledDate: [ moment(new Date()).add(1, 'day').startOf('day'), ValidationUtil.dateAndTimeRequired ],
			Sound: 'default',
			Text: [ '', [ Validators.required, Validators.minLength(4), Validators.maxLength(this.textMaxLength) ] ],
			Title: [ '', [ Validators.required, Validators.minLength(4), Validators.maxLength(this.titleMaxLength) ] ],
			Type: NotificationType.Push,
		});

		ValidationUtil.setupValidation(this, this.editForm, 'Title');
		ValidationUtil.setupValidation(this, this.editForm, 'Text');
		ValidationUtil.setupValidation(this, this.editForm, 'ScheduledDate', 'Send Date');
		this.editForm!.get('Type')!.valueChanges.subscribe(() => this.onTypeChanged());
	}

	static get defaultDate(): string { return moment().startOf('hour').add(1, 'hour').format('YYYY-MM-DD HH:mm:ss'); }

	get value() {
		return this.notification;
	}

	registerOnChange(fn: any): void
	{
		this.editForm!.valueChanges.pipe(map(_ => {
			const value = this.editForm!.getRawValue();
			if (this.selectPdf !== undefined && !this.updatingForm)
			{
				if (this.selectPdf.pdfRemoved)
				{
					value.PdfUrl = undefined;
					value.PdfFile = undefined;
				}
				else
				{
					value.PdfFile = this.selectPdf.uploadFile;
					value.PdfUrl = this.selectPdf.selectedUrl || this.selectPdf.pdfUrl;
				}
			}
			return value;
		})).subscribe(fn);
	}

	registerOnTouched(fn: any): void
	{
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void
	{
		this.disabled = isDisabled;
		isDisabled ? this.editForm!.get('Type')!.disable() : this.editForm!.get('Type')!.enable();
		isDisabled ? this.editForm!.get('Title')!.disable() : this.editForm!.get('Title')!.enable();
		isDisabled ? this.editForm!.get('Text')!.disable() : this.editForm!.get('Text')!.enable();
		isDisabled ? this.editForm!.get('HtmlText')!.disable() : this.editForm!.get('HtmlText')!.enable();
		isDisabled ? this.editForm!.get('Sound')!.disable() : this.editForm!.get('Sound')!.enable();
		isDisabled ? this.editForm!.get('RegistrationFilter')!.disable() : this.editForm!.get('RegistrationFilter')!.enable();
		if (this.notification != undefined && this.notification.IsCampSiteCreated) this.editForm!.get('RegistrationFilter')!.disable()
		this.ScheduledDateTimes.controls.forEach(control =>
		{
			let isDateTimeDisabled = this.controlObjectPairs.find(pair => pair.control === control).object.SentDateTime != null;
			isDateTimeDisabled ? control.disable() : control.enable();
		});
		// isDisabled ? this.editForm!.disable() : this.editForm!.enable();
	}

	writeValue(notification: NotificationItem): void
	{
		if (notification == undefined) return;
		if (!this.allowSms) this.typeChoices = this.typeChoices.filter(c => c.value !== NotificationType.Sms);
		(<any>notification).ScheduledDateTimes = notification.SendInfos.map((si: NotificationSendInfo) => si.ScheduledDateTime);
		this.notification = notification;
		this.updatingForm = true;
		if (this.selectPdf) this.selectPdf.clear();
		this.editForm!.patchValue({
			Categories: notification.CategoryIds == null ? [] : this.organization!.CategoryItems.filter(item => notification.CategoryIds.indexOf(item.Id) !== -1),
			HtmlText: notification.HtmlText,
			MediaUrl: notification.MediaUrl,
			PhotoFile: notification.PhotoFile,
			PdfFile: notification.PdfFile,
			PdfUrl: notification.PdfUrl,
			RegistrationFilter: notification.RegistrationFilter,
			Sound: notification.Sound ?? 'default',
			Text: notification.Text,
			Title: notification.Title,
			Type: notification.Type
		}, { emitEvent: true});
		this.editForm!.setControl('ScheduledDateTimes', this.fb.array(notification.SendInfos.map((si: NotificationSendInfo) => this.buildDate(si))));
		// this.editForm!.setValue(notification, { emitEvent: false });
		this.setDisabledState(notification.IsPublished);
		setTimeout(() =>
		{
			this.editForm!.markAsPristine();
			this.editForm!.markAsUntouched();
		}, 100);
		this.updateMemberCount();
		this.updatingForm = false;
		this.setupImageHandling();
	}

	validate(control: AbstractControl): ValidationErrors | null
	{
		return this.editForm!.valid ? null : { invalidForm: {valid: false, message: "Notification Form fields are invalid"}};
	}

	onHtmlTextChanged(event: any)
	{
		this.editForm!.get('Text')!.setValue(event.textValue);
		this.editForm!.markAsDirty();
		this.changeDetector.detectChanges();
	}

	get selectedCategoryIds(): number[]
	{
		return this.editForm!.get('Categories')!.value.map((c: CategoryItem) => c.Id);
	}

	onChooseCategories()
	{
		this.showSelectCategoriesDialog = true;
	}

	onCategoriesSelected(categoryIds?: number[]): void
	{
		this.showSelectCategoriesDialog = false;
		if (categoryIds == undefined) return;
		this.editForm!.patchValue({
			Categories: this.organization!.CategoryItems.filter(item => categoryIds.some(id => id === item.Id))
		});
		this.updateMemberCount();
		this.editForm!.markAsDirty();
	}

	onRemoveCategories(): void
	{
		this.editForm!.get('Categories')?.setValue([]);
		this.editForm!.markAsDirty();
		this.updateMemberCount();
	}

	onSelectImage(): void
	{
		this.fileInput.nativeElement.click();
	}

	onUploadImage(event: any)
	{
		if (event == null || event.target == null || event.target.files == null || event.target.files.length === 0) { return; }

		const file = event.target.files[0];
		this.imageManager.checkImageSize(file).subscribe(size =>
		{
			if (size.width > 800 || size.height > 800) {
				this.ImageValidationMessage = 'Email images must be no more that 800 pixels by 800 pixels.';
				return;
			}
			this.ImageValidationMessage = undefined;
			this.imageManager.uploadFile(file).subscribe(result =>
			{
				const html = `<img src="${result.fileOrUrl.substring(0, result.fileOrUrl.indexOf('?'))}" style="max-width: 600px"  alt="MMS image"\/>`;
				const range = this.quill.getSelection();
				this.quill.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
			}, () =>
			{
			});
		});
	}

	private setupImageHandling(): void
	{
		setTimeout(() =>
		{
			if (this.editForm!.get('Type')!.value === NotificationType.Email)
			{
				this.quill = this.htmlEditor!.getQuill();
				const toolbar = this.quill.getModule('toolbar');
				toolbar.addHandler('image', this.onSelectImage.bind(this));
				AppTeamUtil.configureQuillClipboard(this.htmlEditor!.getQuill());
			}
		});
	}

	get typeLabel(): string | undefined
	{
		return this.typeChoices.find(t => t.value === this.editForm!.get('Type')!.value)!.label;
	}

	private onTypeChanged(): void
	{
		const type = this.editForm!.get('Type')!.value;
		this.textMaxLength = type === NotificationType.Email ? NotificationItem.EmailTextMaxLength
			: type === NotificationType.Push ? NotificationItem.PushTextMaxLength
				: NotificationItem.SmsTextMaxLength - this.organization.Name.length - 2;

		const text = this.editForm!.get('Text')!;
		const htmlText = this.editForm!.get('HtmlText')!;
		if (type === NotificationType.Email && !this.updatingForm)
		{
			htmlText.setValue(AppTeamUtil.htmlTextFromString(text.value));
		}

		text.setValidators([ Validators.required, Validators.minLength(4), Validators.maxLength(this.textMaxLength) ]);
		htmlText.markAsDirty();
		text.markAsDirty();
		text.updateValueAndValidity();

		const title = this.editForm!.get('Title')!;
		if (type === NotificationType.Email) title.setValidators([ Validators.required, Validators.minLength(4), Validators.maxLength(150) ])
		else title.clearValidators();
		text.markAsDirty();
		title.updateValueAndValidity();
		this.setupImageHandling();
	}

	private updateMemberCount(): void
	{
		if (this.hideCategories) return;
		const selectedCategoryIds = this.editForm!.get('Categories')!.value ? this.editForm!.get('Categories')!.value.map((category: CategoryItem) => category.Id) : [];
		this.memberManager.getMemberCountForCategories(selectedCategoryIds).subscribe(result => this.memberCountResult = result);
	}

	onImageChanged()
	{
		if (this.selectImage.imageRemoved) this.editForm!.get('MediaUrl')!.setValue(undefined);
		this.editForm!.get('PhotoFile')!.setValue(this.selectImage.selectedFile)
	}

	onPdfChanged()
	{
		if (this.selectPdf.pdfRemoved) this.editForm!.get('PdfUrl')!.setValue(undefined);
		this.editForm!.get('PdfFile')!.setValue(this.selectPdf.selectedFileOrUrl instanceof File ? this.selectPdf.selectedFileOrUrl : undefined);
		this.editForm!.get('PdfUrl')!.setValue(typeof this.selectPdf.selectedFileOrUrl === 'string' ? this.selectPdf.selectedFileOrUrl : undefined);
	}

	onPlaySound()
	{
		const audio = new Audio(`${this.SOUND_PATH}${this.editForm!.get('Sound')!.value}`);
		audio.play().then();
	}

	// ===================================================================================================================
	// Date/times support
	// ===================================================================================================================
	get ScheduledDateTimes(): FormArray
	{
		return <FormArray>this.editForm!.get('ScheduledDateTimes');
	}

	buildDate(si: NotificationSendInfo): FormControl
	{
		const value = si.ScheduledDateTime;
		const newControl = new FormControl(value, ValidationUtil.dateAndTimeRequired);
		newControl.valueChanges.pipe(debounceTime(1000)).subscribe( () =>
		{
			const index = this.ScheduledDateTimes.controls.indexOf(newControl);
			this.ScheduledDateValidationMessage[index] = ValidationUtil.getValidationMessage(newControl, "Send Date", "Send Date");
		});
		this.controlObjectPairs.push({ control: newControl, object: si});

		return newControl;
	}

	onAddDate()
	{
		const dates = this.ScheduledDateTimes.value.sort((a: Date, b: Date) => a > b ? 1 : -1);
		const dateString = dates.length === 0 ? EditNotificationControlComponent.defaultDate :
			moment(dates[dates.length - 1]).add(1, 'day').format('YYYY-MM-DD HH:mm:ss');

		const newControl = this.buildDate(new NotificationSendInfo({ ScheduledDateTime: dateString }));
		this.ScheduledDateTimes.push(newControl);
	}

	onDeleteDate(index: number)
	{
		this.controlObjectPairs = this.controlObjectPairs.filter(pair => pair.object !== this.ScheduledDateTimes.controls[index]);
		this.ScheduledDateTimes.removeAt(index);
		this.editForm!.markAsDirty();
	}

	isControlEnabled(control: any): boolean
	{
		return this.controlObjectPairs.find(pair => pair.control === control).object.SentDateTime == null;
	}

	getNumberOfSentUsers(sendInfoIndex: number): string
	{
		if (sendInfoIndex >= this.notification.SendInfos.length || this.notification.SendInfos[sendInfoIndex].SentDateTime == null) return '';
		const count = this.notification.SendInfos[sendInfoIndex].SentUserCount;
		return `(Sent to ${count} user${count === 1 ? '' : 's'})`;
	}
}
